import { useRef, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { StoreState } from "../models/states/StoreState";
import { DefaultFocusStatusModel } from "../models/DefaultFocusStatusModel";
import { setDefaultFocusStatus } from "../redux/actions/index";
import { isGrid, QuestionType } from "../models/enums/QuestionType";

export function useKeyboardNavigation(
    elementId,
    variable,
    question,
    NavigationClass,
    onSelect,
    isProbQuestion = false,
) {
    const elementReference = useRef(null);
    const defaultFocusStatus = useSelector((state: StoreState) => state.defaultFocusStatus);
    const dispatch = useDispatch();

    const navigation = new NavigationClass(defaultFocusStatus, dispatch, question);
    const isHybridGrid =
        question && question.parent && question.parent.type === QuestionType.HybridGrid;
    const rowIndex = isHybridGrid ? question.rowIndex : null;

    function getCurrentElementFromStore(): DefaultFocusStatusModel | undefined {
        return defaultFocusStatus.statuses.find(
            (el) => variable && el.variableId === variable.id && el.elementId === elementId,
        );
    }

    function getQuestionId(): number | null {
        let questionId = null;
        if (question && question.parent && isGrid(question.parent.type)) {
            questionId = question.parent.id;
        } else if (question) {
            questionId = question.id;
        }
        return questionId;
    }

    // Handle focus management
    function handleFocus(shouldAutoFocus: boolean = false) {
        // For AI probe questions, we need the DOM element to exist
        if (isProbQuestion && !elementReference.current) {
            return;
        }

        // Check if this is a valid question element that should receive focus
        const isQuestionElement =
            variable.id && // Has a variable ID
            getQuestionId() && // Has a question ID
            elementReference.current && // Has a valid reference
            !elementReference.current.hasAttribute("disabled") && // Not disabled
            elementReference.current.getAttribute("tabindex") !== "-1"; // Is focusable

        // Register the element if not already registered. If already registered,
        // ensure the stored elementRef points to this component's ref so it stays
        // valid after remounts / hide-unhide cycles.
        const currentElementStatus = getCurrentElementFromStore();
        if (currentElementStatus) {
            // Update ref if identity changed (component remount created a new ref)
            if (currentElementStatus.elementRef !== elementReference) {
                currentElementStatus.elementRef = elementReference;
                // push the update to Redux so navigation code uses the fresh ref
                dispatch(setDefaultFocusStatus({ ...defaultFocusStatus }));
            }
        }

        if (!currentElementStatus) {
            if (isProbQuestion) {
                const aiProbeParentIndex = defaultFocusStatus.statuses.findIndex(
                    (status) => status.questionId === question.id,
                );
                if (aiProbeParentIndex !== -1) {
                    const newElementStatus = {
                        elementId,
                        elementRef: elementReference,
                        variableId: variable.id,
                        questionId: getQuestionId(),
                        focusStatus: false,
                        isQuestionElement: false,
                    };
                    const focusStatus = defaultFocusStatus.statuses.some(
                        (status) => status.focusStatus && status.elementId.includes("_AI_PROBE"),
                    );
                    if (!focusStatus) {
                        elementReference.current.scrollIntoView({
                            behavior: "smooth",
                            block: "center",
                        });
                        newElementStatus.focusStatus = true;
                        elementReference.current.focus();
                    }
                    defaultFocusStatus.statuses.splice(aiProbeParentIndex + 1, 0, newElementStatus);
                    dispatch(setDefaultFocusStatus({ ...defaultFocusStatus }));
                }
            } else {
                // For regular elements, we can register even without DOM element
                defaultFocusStatus.statuses.push({
                    elementId,
                    elementRef: elementReference,
                    variableId: variable.id,
                    questionId: getQuestionId(),
                    rowIndex,
                    focusStatus: false,
                    isQuestionElement: elementReference.current ? isQuestionElement : false,
                });
                dispatch(setDefaultFocusStatus({ ...defaultFocusStatus }));
            }
        }

        // Only handle auto-focus if DOM element exists
        if (!elementReference.current) {
            return;
        }

        // Only handle focus if this is a question element, autofocus is requested, and we're in an iframe
        const isInIframe = window !== window.parent;
        if (isQuestionElement && shouldAutoFocus && isInIframe) {
            const firstQuestionElement = defaultFocusStatus.statuses.find(
                (status) => status.isQuestionElement,
            );

            const isFirstQuestion =
                firstQuestionElement &&
                firstQuestionElement.elementId === elementId &&
                firstQuestionElement.variableId === variable.id;

            if (isFirstQuestion) {
                // Reset all focus statuses and focus first question
                defaultFocusStatus.statuses.forEach((status) => (status.focusStatus = false));
                elementReference.current.focus();
                elementReference.current.scrollIntoView({ behavior: "smooth", block: "center" });

                // Update focus status for this element
                const updatedStatus = getCurrentElementFromStore();
                if (updatedStatus) {
                    const index = defaultFocusStatus.statuses.indexOf(updatedStatus);
                    defaultFocusStatus.statuses[index].focusStatus = true;
                    dispatch(setDefaultFocusStatus({ ...defaultFocusStatus }));
                }
            }
        }
    }

    // Handle visibility changes
    useEffect(() => {
        function checkIframeVisibility() {
            const isInIframe = window !== window.parent;

            if (isInIframe) {
                const observer = new IntersectionObserver(
                    (entries) => {
                        entries.forEach((entry) => {
                            if (entry.isIntersecting) {
                                const computedStyle = window.getComputedStyle(
                                    document.documentElement,
                                );
                                const isVisible =
                                    computedStyle.display !== "none" &&
                                    computedStyle.visibility !== "hidden";

                                if (isVisible) {
                                    handleFocus(true);
                                }
                            }
                        });
                    },
                    { threshold: 0 },
                );

                observer.observe(document.documentElement);
                return () => observer.disconnect();
            }
        }

        const cleanup = checkIframeVisibility();
        return () => {
            if (cleanup) cleanup();
        };
    }, []);

    // Handle initial mount
    useEffect(() => {
        if (variable && defaultFocusStatus && defaultFocusStatus.statuses) {
            const shouldAutoFocus = !defaultFocusStatus.statuses.some(
                (status) => status.focusStatus,
            );
            handleFocus(shouldAutoFocus);
        }
    }, []);

    function onKeyDown(
        event: React.KeyboardEvent<
            HTMLDivElement | HTMLTextAreaElement | HTMLButtonElement | Element
        >,
    ) {
        if (event.target !== event.currentTarget) return;

        if (event.key === " " && onSelect) {
            onSelect();
            event.preventDefault();

            // Set the focus to current element even in case of element selection as well
            const currentElementInfo = getCurrentElementFromStore();
            const index = defaultFocusStatus.statuses.indexOf(currentElementInfo);
            defaultFocusStatus.statuses[index].focusStatus = true;
            dispatch(setDefaultFocusStatus(defaultFocusStatus));
        } else {
            const currentElementInfo = getCurrentElementFromStore();
            if (currentElementInfo) {
                navigation.handleKeyPressEvent(event, currentElementInfo);
            }
        }
    }

    return { elementReference, onKeyDown };
}
